import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { PaymentsSettingsDraft } from "../../store/paymentsSettingsSlice";
import type { CancellationWindow, RefundPolicy } from "../../api/payments";

interface PaymentModeFormProps {
  draft: PaymentsSettingsDraft;
  fieldErrors: Record<string, string>;
  formError: string | null;
  isSaving: boolean;
  isDirty: boolean;
  onUpdate: (patch: Partial<PaymentsSettingsDraft>) => void;
  onSave: () => void;
  onCancel: () => void;
  paymentsRequired?: boolean;
  depositMinFixedMinorUnits?: number;
  depositMinPercentBps?: number;
}

export const PaymentModeForm: React.FC<PaymentModeFormProps> = ({
  draft,
  fieldErrors,
  formError,
  isSaving,
  isDirty,
  onUpdate,
  onSave,
  onCancel,
  paymentsRequired = false,
  depositMinFixedMinorUnits = 0,
  depositMinPercentBps = 0,
}) => {
  const showDepositFields = draft.payment_mode === "deposit_required";
  const showCancellationFields =
    draft.payment_mode === "deposit_required" ||
    draft.payment_mode === "full_payment_required";
  const showFixedDepositFloorHint =
    paymentsRequired &&
    draft.payment_mode === "deposit_required" &&
    draft.deposit_type === "fixed" &&
    depositMinFixedMinorUnits > 0;
  const showPercentDepositFloorHint =
    paymentsRequired &&
    draft.payment_mode === "deposit_required" &&
    draft.deposit_type === "percent" &&
    depositMinPercentBps > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Payment Settings
        </Typography>

        {paymentsRequired && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="payments-required-banner">
            Your plan requires online payments.
          </Alert>
        )}

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {/* Payment mode */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Payment mode
          </Typography>
          <RadioGroup
            aria-label="payment mode"
            value={draft.payment_mode}
            onChange={(e) =>
              onUpdate({ payment_mode: e.target.value as PaymentsSettingsDraft["payment_mode"] })
            }
          >
            <FormControlLabel
              value="disabled"
              control={<Radio />}
              label="Payments disabled"
              disabled={paymentsRequired}
            />
            <FormControlLabel
              value="deposit_required"
              control={<Radio />}
              label="Deposit required"
            />
            <FormControlLabel
              value="full_payment_required"
              control={<Radio />}
              label="Full payment required"
            />
          </RadioGroup>
        </FormControl>

        {/* Deposit fields */}
        {showDepositFields && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Deposit configuration
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="deposit-type-label">Deposit type</InputLabel>
                <Select
                  labelId="deposit-type-label"
                  inputProps={{ "aria-label": "Deposit type" }}
                  label="Deposit type"
                  value={draft.deposit_type ?? ""}
                  onChange={(e) =>
                    onUpdate({ deposit_type: e.target.value as PaymentsSettingsDraft["deposit_type"] })
                  }
                >
                  <MenuItem value="fixed">Fixed amount</MenuItem>
                  <MenuItem value="percent">Percentage</MenuItem>
                </Select>
              </FormControl>

              {draft.deposit_type === "fixed" && (
                <Box>
                  <TextField
                    size="small"
                    label="Deposit amount"
                    type="number"
                    value={
                      draft.deposit_value_minor_units != null
                        ? draft.deposit_value_minor_units / 100
                        : ""
                    }
                    onChange={(e) =>
                      onUpdate({
                        deposit_value_minor_units: Math.round(
                          parseFloat(e.target.value || "0") * 100
                        ),
                      })
                    }
                    error={Boolean(fieldErrors.deposit_value_minor_units)}
                    helperText={fieldErrors.deposit_value_minor_units}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    inputProps={{ "aria-label": "Deposit amount" }}
                  />
                  {showFixedDepositFloorHint && (
                    <FormHelperText>
                      Plan minimum: ${(depositMinFixedMinorUnits / 100).toFixed(2)}
                    </FormHelperText>
                  )}
                </Box>
              )}

              {draft.deposit_type === "percent" && (
                <Box>
                  <TextField
                    size="small"
                    label="Deposit percentage"
                    type="number"
                    value={
                      draft.deposit_percent_bps != null
                        ? draft.deposit_percent_bps / 100
                        : ""
                    }
                    onChange={(e) =>
                      onUpdate({
                        deposit_percent_bps: Math.round(
                          parseFloat(e.target.value || "0") * 100
                        ),
                      })
                    }
                    error={Boolean(fieldErrors.deposit_percent_bps)}
                    helperText={fieldErrors.deposit_percent_bps}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    inputProps={{ "aria-label": "Deposit percentage" }}
                  />
                  {showPercentDepositFloorHint && (
                    <FormHelperText>
                      Plan minimum: {depositMinPercentBps / 100}%
                    </FormHelperText>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Cancellation policy */}
        {showCancellationFields && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Cancellation policy
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="cancel-window-label">
                  Cancellation window
                </InputLabel>
                <Select
                  labelId="cancel-window-label"
                  label="Cancellation window"
                  value={draft.cancellation_window_hours ?? ""}
                  onChange={(e) =>
                    onUpdate({
                      cancellation_window_hours: (
                        e.target.value !== ""
                          ? Number(e.target.value)
                          : null
                      ) as CancellationWindow | null,
                      cancellation_policy_enabled: e.target.value !== "",
                    })
                  }
                  inputProps={{ "aria-label": "Cancellation window" }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value={12}>12 hours</MenuItem>
                  <MenuItem value={24}>24 hours</MenuItem>
                  <MenuItem value={48}>48 hours</MenuItem>
                  <MenuItem value={72}>72 hours</MenuItem>
                </Select>
                {fieldErrors.cancellation_window_hours && (
                  <FormHelperText error>
                    {fieldErrors.cancellation_window_hours}
                  </FormHelperText>
                )}
              </FormControl>

              {showDepositFields && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="refund-policy-label">
                    Deposit refund policy
                  </InputLabel>
                  <Select
                    labelId="refund-policy-label"
                    label="Deposit refund policy"
                    value={draft.deposit_refund_policy ?? ""}
                    onChange={(e) =>
                      onUpdate({
                        deposit_refund_policy: (
                          e.target.value || null
                        ) as RefundPolicy | null,
                      })
                    }
                    inputProps={{ "aria-label": "Deposit refund policy" }}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="full_refund">Full refund</MenuItem>
                    <MenuItem value="no_refund">No refund</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="text"
            disabled={isSaving}
            onClick={onCancel}
            aria-label="Cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isDirty || isSaving}
            onClick={onSave}
            aria-label="Save payment settings"
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
